import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import styles from "./AuroraVaultPaywall.module.scss"

/**
 * AuroraVaultPaywall — an original, self-contained ONBOARDING paywall.
 *
 * Concept ("Memories in orbit"): the first thing a new user sees is their
 * private world rendered as a small galaxy — a glowing vault core with their
 * memories orbiting it inside an aurora-lit cosmos. The whole screen is built
 * to funnel toward the YEARLY plan: yearly is the only prominent, glowing
 * purchase surface (priced per-week to feel tiny), the savings vs monthly is
 * shouted, and monthly is demoted to a single quiet line of text.
 *
 * Fully self-contained: no shared visual components are reused. It reads real
 * pricing from the app's debug product data and talks to the native bridge
 * directly, so it renders standalone with the built-in debug data.
 */

const FEATURE_PILLS = [
  { icon: "☁︎", label: "Encrypted Backup" },
  { icon: "◎", label: "Break-in Alerts" },
  { icon: "❖", label: "Decoy Vault" },
  { icon: "✦", label: "Disguised App" },
]

// Memory tiles that ride the orbit rings — pure gradients so the screen is
// self-contained and ships no bespoke imagery.
const ORBIT_MEMORIES = [
  "linear-gradient(135deg,#ff9a7b,#ff5e8a)",
  "linear-gradient(135deg,#7afcff,#2b5ff5)",
  "linear-gradient(135deg,#c2ffd8,#465efb)",
  "linear-gradient(135deg,#ffd56b,#ff7e5f)",
  "linear-gradient(135deg,#b388ff,#5b7ad2)",
  "linear-gradient(135deg,#f6d365,#fda085)",
]

const STAR_COUNT = 70

const AuroraVaultPaywall = () => {
  const { state, actions } = useAppState()
  const productData = state.productData

  const [yearlySelected, setYearlySelected] = useState(true)
  const [showClose, setShowClose] = useState(false)

  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setFooterInsetColor("#05030f")
    actions.setHeaderInsetColor("#05030f")
    const t = setTimeout(() => setShowClose(true), 2600)
    return () => clearTimeout(t)
  }, [actions])

  const premium = productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const perWeek = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 52, locale)
    : "$0.69"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 78
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.round((1 - ratio) * 100)
  }, [yearlyProduct, monthlyProduct])

  const trialDays = yearlyProduct?.introPricing?.period ?? 7
  const hasTrial = (yearlyProduct?.hasTrial ?? true) && trialDays > 0

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  const handleClose = () => native.postNativeEvent("close")

  const handlePurchase = () => {
    const product: Product | undefined = yearlySelected
      ? yearlyProduct
      : monthlyProduct
    if (product) native.subscribe(product)
  }

  const ctaLabel = hasTrial
    ? `Start my ${trialDays} days free`
    : "Unlock my vault"

  return (
    <div className={styles.screen}>
      <Cosmos />

      <AnimatePresence>
        {showClose && (
          <motion.button
            className={styles.close}
            style={{ top: topInset + 14 }}
            onClick={handleClose}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.55, scale: 1 }}
            whileTap={{ scale: 0.85, opacity: 1 }}
            aria-label="Close"
          >
            ✕
          </motion.button>
        )}
      </AnimatePresence>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 18, paddingBottom: bottomInset + 14 }}
      >
        <Orbit />

        <motion.div
          className={styles.heading}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className={styles.title}>
            Your whole world,
            <br />
            <span className={styles.titleAccent}>locked in orbit.</span>
          </h1>
          <p className={styles.subtitle}>
            Photos, videos and secrets — sealed behind military-grade encryption
            that only you can open.
          </p>
        </motion.div>

        <ProtectedCounter />

        <motion.div
          className={styles.pills}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          {FEATURE_PILLS.map((p, i) => (
            <motion.div
              key={p.label}
              className={styles.pill}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75 + i * 0.08, duration: 0.5 }}
            >
              <span className={styles.pillIcon}>{p.icon}</span>
              {p.label}
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className={styles.plans}
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
        >
          <button
            className={`${styles.yearCard} ${
              yearlySelected ? styles.yearCardActive : ""
            }`}
            onClick={() => setYearlySelected(true)}
          >
            <span className={styles.bestRibbon}>SAVE {savings}%</span>
            <div className={styles.yearTop}>
              <div className={styles.radio}>
                <span
                  className={`${styles.radioDot} ${
                    yearlySelected ? styles.radioDotOn : ""
                  }`}
                />
              </div>
              <div className={styles.yearText}>
                <span className={styles.yearName}>
                  {hasTrial ? `${trialDays} days free` : "Yearly access"}
                  <span className={styles.yearThen}>
                    {hasTrial
                      ? `, then ${yearlyPrice}/yr`
                      : ` · ${yearlyPrice}/yr`}
                  </span>
                </span>
                <span className={styles.yearSub}>
                  Full vault · billed once a year
                </span>
              </div>
            </div>
            <div className={styles.perWeekTag}>
              <span className={styles.perWeekValue}>{perWeek}</span>
              <span className={styles.perWeekUnit}>/week</span>
            </div>
          </button>

          <button
            className={styles.monthlyLine}
            onClick={() => setYearlySelected(false)}
          >
            <span
              className={`${styles.monthlyDot} ${
                !yearlySelected ? styles.monthlyDotOn : ""
              }`}
            />
            Prefer monthly? {monthlyPrice}/mo — no discount
          </button>
        </motion.div>

        <motion.div
          className={styles.ctaWrap}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.6 }}
        >
          <button className={styles.cta} onClick={handlePurchase}>
            <span className={styles.ctaShimmer} />
            <span className={styles.ctaLabel}>{ctaLabel}</span>
            <span className={styles.ctaArrow}>→</span>
          </button>
          <p className={styles.ctaNote}>
            {hasTrial
              ? "No charge today · Cancel anytime in one tap"
              : "Cancel anytime · Instant access"}
          </p>
        </motion.div>

        <TrustRow />

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </div>
    </div>
  )
}

/** Animated cosmos backdrop: drifting aurora ribbons + a twinkling starfield. */
const Cosmos = () => {
  const stars = useMemo(
    () =>
      Array.from({ length: STAR_COUNT }, (_, i) => {
        // deterministic pseudo-random so SSR/debug renders are stable
        const r1 = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1
        const r2 = Math.abs(Math.sin(i * 78.233) * 12543.123) % 1
        const r3 = Math.abs(Math.sin(i * 4.123) * 9973.77) % 1
        return {
          left: `${r1 * 100}%`,
          top: `${r2 * 100}%`,
          size: 1 + r3 * 1.8,
          delay: `${r1 * 4}s`,
          dur: `${2 + r2 * 3}s`,
        }
      }),
    [],
  )

  return (
    <div className={styles.cosmos} aria-hidden>
      <div className={styles.auroraA} />
      <div className={styles.auroraB} />
      <div className={styles.auroraC} />
      <div className={styles.vignette} />
      <div className={styles.stars}>
        {stars.map((s, i) => (
          <span
            key={i}
            className={styles.star}
            style={{
              left: s.left,
              top: s.top,
              width: s.size,
              height: s.size,
              animationDelay: s.delay,
              animationDuration: s.dur,
            }}
          />
        ))}
      </div>
    </div>
  )
}

/** The hero: a breathing vault core with memories orbiting on two rings. */
const Orbit = () => {
  return (
    <motion.div
      className={styles.orbit}
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className={styles.ringOuter}>
        {ORBIT_MEMORIES.slice(0, 4).map((g, i) => (
          <span
            key={i}
            className={styles.memory}
            style={{
              background: g,
              transform: `rotate(${i * 90}deg) translateX(118px) rotate(-${
                i * 90
              }deg)`,
            }}
          />
        ))}
      </div>
      <div className={styles.ringInner}>
        {ORBIT_MEMORIES.slice(0, 3).map((g, i) => (
          <span
            key={i}
            className={styles.memorySmall}
            style={{
              background: g,
              transform: `rotate(${i * 120}deg) translateX(72px) rotate(-${
                i * 120
              }deg)`,
            }}
          />
        ))}
      </div>

      <div className={styles.core}>
        <div className={styles.coreGlow} />
        <div className={styles.coreShield}>
          <svg viewBox="0 0 24 24" width="34" height="34" fill="none">
            <path
              d="M12 2.5 4.5 5.5v5.2c0 4.6 3.1 8.9 7.5 10.3 4.4-1.4 7.5-5.7 7.5-10.3V5.5L12 2.5Z"
              fill="rgba(255,255,255,0.95)"
            />
            <path
              d="M12 11.2a1.9 1.9 0 0 0-.9 3.6v1.7a.9.9 0 0 0 1.8 0v-1.7a1.9 1.9 0 0 0-.9-3.6Z"
              fill="#2b5ff5"
            />
            <path
              d="M9.4 10.2V8.9a2.6 2.6 0 0 1 5.2 0v1.3"
              stroke="#2b5ff5"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
    </motion.div>
  )
}

/** A live "memories sealed today" ticker for momentum / social proof. */
const ProtectedCounter = () => {
  const [count, setCount] = useState(12840)
  const ref = useRef(12840)

  useEffect(() => {
    const id = setInterval(() => {
      ref.current += Math.floor(1 + Math.abs(Math.sin(ref.current)) * 3)
      setCount(ref.current)
    }, 900)
    return () => clearInterval(id)
  }, [])

  return (
    <motion.div
      className={styles.counter}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.55, duration: 0.6 }}
    >
      <span className={styles.counterPulse} />
      <span className={styles.counterValue}>{count.toLocaleString()}</span>
      <span className={styles.counterLabel}>memories sealed today</span>
    </motion.div>
  )
}

const TrustRow = () => {
  return (
    <motion.div
      className={styles.trust}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.1, duration: 0.6 }}
    >
      <div className={styles.trustItem}>
        <span className={styles.stars5}>★★★★★</span>
        <span className={styles.trustLabel}>4.8 App Store</span>
      </div>
      <div className={styles.trustDivider} />
      <div className={styles.trustItem}>
        <span className={styles.trustBig}>10M+</span>
        <span className={styles.trustLabel}>vaults trusted</span>
      </div>
      <div className={styles.trustDivider} />
      <div className={styles.trustItem}>
        <span className={styles.trustBig}>🔒</span>
        <span className={styles.trustLabel}>zero-knowledge</span>
      </div>
    </motion.div>
  )
}

export default AuroraVaultPaywall
